<script setup lang="ts">
import type { HTMLAttributes, InputTypeHTMLAttribute } from 'vue'
import { cn } from '@/utils/shadcn/lib/utils'
import { useVModel } from '@vueuse/core'

defineOptions({
    compatConfig: {
        MODE: 3,
    },
    inheritAttrs: true,
})

const props = defineProps<{
    defaultValue?: string | number
    modelValue?: string | number
    class?: HTMLAttributes['class']
    type?: InputTypeHTMLAttribute
    required?: boolean
    invalid?: boolean
    disabled?: boolean
}>()

const emits = defineEmits<{
  'update:modelValue': [payload: string | number]
}>()

const modelValue = useVModel(props, 'modelValue', emits, {
    passive: true,
    defaultValue: props.defaultValue,
})

function onWheel(event: WheelEvent) {
    if (props.type === 'number' && event.currentTarget === document.activeElement) {
        event.preventDefault();
    }
}
</script>

<template>
    <input
        v-model="modelValue"
        data-slot="input"
        class="sph-form-control"
        :class="cn(
            'text-input-foreground file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground border-input-dark h-[calc(var(--input-height)-2px)] min-w-0 rounded-md border bg-input px-3 mt-1 text-base leading-tight shadow-xs transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium',
            props.class,
        )"
        :required="props.required"
        :aria-invalid="props.invalid ? 'true' : 'false'"
        :disabled="props.disabled"
        :type="props.type"
        @wheel="onWheel"
    />
</template>

<style scoped>
input[type='number']::-webkit-outer-spin-button,
input[type='number']::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
}

input[type='number'] {
    -moz-appearance: textfield;
}

input:-webkit-autofill,
input:-webkit-autofill:hover,
input:-webkit-autofill:focus {
    -webkit-box-shadow: 0 0 0px 1000px var(--color-input) inset;
    -webkit-text-fill-color: var(--color-input-foreground);
}
</style>
